/**
 * @fileoverview Servicio para generación y manejo de PDFs
 * @description Maneja la creación, descarga y subida de PDFs
 */

import jsPDF from 'jspdf';
import { Sociedad, IAccionFormativa, ICursoAccionFormativa, ICurso, IPoliza } from '../types';
import { buildApiUrl, API_ENDPOINTS, getUploadHeaders, API_BASE_URL } from '../config/api';

/**
 * Interfaz para la respuesta de subida de PDF
 */
export interface PdfUploadResponse {
  success: boolean;
  message: string;
  filename: string;
  path: string;
  data: {
    sociedadId: number;
    tipo: string;
    originalName: string;
    size: number;
    mimetype: string;
    uploadDate: string;
  };
}

/**
 * Sanitiza el nombre del archivo para evitar caracteres problemáticos
 * @param razonSocial - Razón social de la sociedad
 * @returns Nombre de archivo sanitizado
 */
const sanitizeFilename = (razonSocial: string): string => {
  return razonSocial
    .replace(/[^a-zA-Z0-9\s\-_]/g, '') // Eliminar caracteres especiales excepto espacios, guiones y guiones bajos
    .replace(/\s+/g, '-') // Reemplazar espacios con guiones
    .substring(0, 50); // Limitar longitud
};

/**
 * Convierte HTML a texto plano preservando saltos de línea y estructura
 * @param html - String HTML a convertir
 * @returns Texto plano con saltos de línea preservados
 */
const htmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return 'No especificado';

  // Si está vacío o solo contiene etiquetas vacías, retornar mensaje por defecto
  const trimmedHtml = html.trim();
  if (!trimmedHtml || trimmedHtml === '' || trimmedHtml === '<p><br></p>' || trimmedHtml === '<p><br /></p>') {
    return 'No especificado';
  }

  // Crear un elemento temporal para parsear el HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Función recursiva para procesar nodos y preservar estructura
  const processNode = (node: Node, result: string[]): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim()) {
        result.push(text);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Manejar elementos especiales
      if (tagName === 'br' || tagName === 'br/') {
        result.push('\n');
        return;
      }

      // Procesar nodos hijos
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach((child) => {
        processNode(child, result);
      });

      // Agregar saltos de línea después de ciertos elementos de bloque
      if (tagName === 'p' || tagName === 'div') {
        result.push('\n');
      } else if (tagName === 'li') {
        result.push('\n');
      }
    }
  };

  // Procesar todos los nodos
  const result: string[] = [];
  Array.from(tempDiv.childNodes).forEach(node => {
    processNode(node, result);
  });

  // Unir el resultado y limpiar espacios múltiples (pero preservar saltos de línea)
  const text = result.join('')
    .replace(/[ \t]+/g, ' ') // Reemplazar múltiples espacios/tabs con un solo espacio
    .replace(/\n\s+/g, '\n') // Eliminar espacios al inicio de líneas
    .replace(/\s+\n/g, '\n') // Eliminar espacios al final de líneas
    .replace(/\n{3,}/g, '\n\n') // Reemplazar 3+ saltos de línea con solo 2
    .trim();

  // Si está vacío después de procesar, retornar mensaje por defecto
  if (!text || text === '') {
    return 'No especificado';
  }

  return text;
};

/**
 * Interfaz para opciones de renderizado de HTML en PDF
 */
interface RenderHtmlOptions {
  pdf: jsPDF;
  x: number;
  y: number;
  maxWidth: number;
  pageHeight: number;
  baseFontSize?: number;
  lineHeight?: number;
}

/**
 * Renderiza HTML con formato en el PDF respetando los estilos de ReactQuill
 * @param html - String HTML a renderizar
 * @param options - Opciones de renderizado
 * @returns Nueva posición Y después de renderizar
 */
const renderHtmlToPdf = (html: string | null | undefined, options: RenderHtmlOptions): number => {
  const { pdf, x, y: startY, maxWidth, pageHeight, baseFontSize = 10, lineHeight = 6 } = options;

  if (!html) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(baseFontSize);
    pdf.text('No especificado', x, startY);
    return startY + lineHeight;
  }

  // Si está vacío o solo contiene etiquetas vacías
  const trimmedHtml = html.trim();
  if (!trimmedHtml || trimmedHtml === '' || trimmedHtml === '<p><br></p>' || trimmedHtml === '<p><br /></p>') {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(baseFontSize);
    pdf.text('No especificado', x, startY);
    return startY + lineHeight;
  }

  // Crear un elemento temporal para parsear el HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  let currentY = startY;
  let listCounter = 0; // Contador para listas ordenadas
  let inOrderedList = false;
  let inUnorderedList = false;
  let inListItem = false; // Indica si estamos dentro de un elemento <li>
  let listItemX = x; // Posición X para el contenido de listas

  /**
   * Renderiza un nodo de texto con formato
   */
  const renderTextNode = (text: string, format: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: number;
  }, customX?: number): void => {
    if (!text || text.trim() === '') return;

    // Verificar si necesita nueva página
    if (currentY > pageHeight - 40) {
      pdf.addPage();
      currentY = 30;
    }

    // Aplicar formato
    let fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal';
    if (format.bold && format.italic) {
      fontStyle = 'bolditalic';
    } else if (format.bold) {
      fontStyle = 'bold';
    } else if (format.italic) {
      fontStyle = 'italic';
    }

    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(format.fontSize || baseFontSize);

    // Usar posición X personalizada si se proporciona, o la posición de lista si estamos en un <li>
    const textX = customX !== undefined ? customX : (inListItem ? listItemX : x);
    const availableWidth = inListItem ? maxWidth - (listItemX - x) : maxWidth;

    // Dividir el texto si es muy largo
    const splitText = pdf.splitTextToSize(text.trim(), availableWidth);

    splitText.forEach((line: string, index: number) => {
      if (index > 0 && currentY > pageHeight - 40) {
        pdf.addPage();
        currentY = 30;
      }

      pdf.text(line, textX, currentY);

      // Simular subrayado dibujando una línea
      if (format.underline) {
        const textWidth = pdf.getTextWidth(line);
        const underlineY = currentY + 1.5;
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.1);
        pdf.line(textX, underlineY, textX + textWidth, underlineY);
      }

      currentY += lineHeight;
    });
  };

  /**
   * Procesa recursivamente los nodos del DOM
   */
  const processNode = (node: Node, format: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: number;
  }): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim()) {
        renderTextNode(text, format);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Manejar headers
      if (tagName === 'h1') {
        processNodeChildren(element, { ...format, fontSize: baseFontSize + 4, bold: true });
        currentY += lineHeight * 0.5; // Espacio adicional después de headers
      } else if (tagName === 'h2') {
        processNodeChildren(element, { ...format, fontSize: baseFontSize + 3, bold: true });
        currentY += lineHeight * 0.5;
      } else if (tagName === 'h3') {
        processNodeChildren(element, { ...format, fontSize: baseFontSize + 2, bold: true });
        currentY += lineHeight * 0.5;
      }
      // Manejar párrafos
      else if (tagName === 'p') {
        const children = Array.from(element.childNodes);
        if (children.length === 0 || (children.length === 1 && children[0].nodeType === Node.ELEMENT_NODE && (children[0] as Element).tagName.toLowerCase() === 'br')) {
          // Párrafo vacío, solo agregar espacio
          currentY += lineHeight * 0.5;
        } else {
          processNodeChildren(element, format);
          currentY += lineHeight * 0.3; // Espacio después del párrafo
        }
      }
      // Manejar listas ordenadas
      else if (tagName === 'ol') {
        const previousCounter = listCounter;
        const previousOrdered = inOrderedList;
        listCounter = 1;
        inOrderedList = true;
        processNodeChildren(element, format);
        listCounter = previousCounter;
        inOrderedList = previousOrdered;
        currentY += lineHeight * 0.3;
      }
      // Manejar listas no ordenadas
      else if (tagName === 'ul') {
        const previousUnordered = inUnorderedList;
        inUnorderedList = true;
        processNodeChildren(element, format);
        inUnorderedList = previousUnordered;
        currentY += lineHeight * 0.3;
      }
      // Manejar elementos de lista
      else if (tagName === 'li') {
        if (currentY > pageHeight - 40) {
          pdf.addPage();
          currentY = 30;
        }

        // Calcular posición para la viñeta/número y el contenido
        const bulletX = x;
        const contentX = x + 12; // Espacio después de la viñeta/número
        const previousListItemX = listItemX;
        const previousInListItem = inListItem;

        listItemX = contentX;
        inListItem = true;

        // Agregar viñeta o número
        if (inOrderedList) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(baseFontSize);
          const counterText = `${listCounter}.`;
          pdf.text(counterText, bulletX, currentY);
          processNodeChildren(element, format);
          listCounter++;
        } else if (inUnorderedList) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(baseFontSize);
          pdf.text('•', bulletX, currentY);
          processNodeChildren(element, format);
        } else {
          processNodeChildren(element, format);
        }

        // Restaurar estado anterior
        listItemX = previousListItemX;
        inListItem = previousInListItem;

        currentY += lineHeight * 0.2;
      }
      // Manejar saltos de línea
      else if (tagName === 'br' || tagName === 'br/') {
        currentY += lineHeight;
      }
      // Manejar formato de texto
      else if (tagName === 'strong' || tagName === 'b') {
        processNodeChildren(element, { ...format, bold: true });
      } else if (tagName === 'em' || tagName === 'i') {
        processNodeChildren(element, { ...format, italic: true });
      } else if (tagName === 'u') {
        processNodeChildren(element, { ...format, underline: true });
      }
      // Procesar otros elementos
      else {
        processNodeChildren(element, format);
      }
    }
  };

  /**
   * Procesa los nodos hijos de un elemento
   */
  const processNodeChildren = (element: Element, format: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: number;
  }): void => {
    Array.from(element.childNodes).forEach(child => {
      processNode(child, format);
    });
  };

  // Procesar todos los nodos raíz
  Array.from(tempDiv.childNodes).forEach(node => {
    processNode(node, {
      bold: false,
      italic: false,
      underline: false,
      fontSize: baseFontSize
    });
  });

  return currentY;
};

/**
 * Servicio para generación de PDFs
 */
export const pdfService = {
  /**
   * Agrega una etiqueta y valor con ajuste de línea
   * @param pdf - Objeto PDF
   * @param label - Etiqueta
   * @param value - Valor
   * @param yPosition - Posición Y actual
   * @param pageWidth - Ancho de la página
   * @param margin - Margen
   * @param valueXOffset - Desplazamiento X para el valor
   * @returns Nueva posición Y
   */
  addWrappedKeyValue(
    pdf: jsPDF,
    label: string,
    value: string,
    yPosition: number,
    pageWidth: number,
    margin: number,
    valueXOffset: number = 60
  ): number {
    const valueX = margin + valueXOffset;
    const availableWidth = pageWidth - margin - valueX;
    const displayValue = value || 'No especificado';
    const lineHeight = 6;

    // Asegurar fuente normal para cálculo
    pdf.setFont('helvetica', 'normal');
    const splitValue = pdf.splitTextToSize(displayValue, availableWidth);

    // Altura requerida para este bloque
    const blockHeight = splitValue.length * lineHeight;
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Verificar si cabe, si no, nueva página
    if (this.necesitaNuevaPagina(yPosition + blockHeight, pageHeight)) {
      this.nuevaPagina(pdf, pageWidth, margin);
      yPosition = 30;
    }

    // Renderizar Label
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin, yPosition);

    // Renderizar Valor
    pdf.setFont('helvetica', 'normal');
    splitValue.forEach((line: string, index: number) => {
      pdf.text(line, valueX, yPosition + (index * lineHeight));
    });

    // Retornar nueva posición Y
    return yPosition + blockHeight;
  },

  /**
   * Genera un PDF con el resumen de la solicitud
   * @param data - Datos completos de la solicitud
   * @returns Promise con el PDF generado
   */
  async generarPDFResumen(data: {
    sociedad: Sociedad;
    accionFormativa?: IAccionFormativa;
    cursosAccionFormativa?: ICursoAccionFormativa[];
    curso?: ICurso;
    poliza?: IPoliza;
  }): Promise<jsPDF> {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = 40; // Más espacio para el logo

    // Agregar logo en la esquina superior derecha
    await this.agregarLogo(pdf, pageWidth, margin);

    // Línea separadora
    pdf.setLineWidth(0.10);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Configurar fuente
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);

    // Título principal
    const text = 'SOLICITUD DE PÓLIZAS DE SEGUROS DE "RESPONSABILIDAD CIVIL y/o ACCIDENTES" PARA CURSOS DE FORMACIÓN';
    const maxWidth = pageWidth - margin * 2;
    const splitText = pdf.splitTextToSize(text, maxWidth);

    // Centrar el texto
    pdf.setTextColor(254, 124, 3);
    pdf.text(splitText, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += splitText.length * 6 + 5; // Ajustar según número de líneas

    // Línea separadora
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(254, 124, 3);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 0);

    // Datos de la Sociedad
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DATOS DEL SOLICITANTE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);

    const sociedadData = [
      ['Razón Social:', data.sociedad.razonSocial],
      ['CIF/NIF:', data.sociedad.cif.toUpperCase()],
      ['Dirección:', data.sociedad.direccion],
      ['Localidad:', data.sociedad.localidad],
      ['Provincia:', data.sociedad.provincia],
      ['Código Postal:', data.sociedad.codigoPostal],
      ['Persona de Contacto:', data.sociedad.personaContacto],
      ['Teléfono:', data.sociedad.telefono],
      ['Email:', data.sociedad.email],
      ['Asociación:', this.getAsociacionName(data.sociedad.condicionesIdAsociacion)],
      ['Archivo de Resolución:', data.sociedad.fileResolucion || 'No proporcionado'],
      ['Archivo de Texto de Seguros:', data.sociedad.fileTextoSeguros || 'No proporcionado'],
    ];

    sociedadData.forEach(([label, value]) => {
      yPosition = this.addWrappedKeyValue(pdf, label, value, yPosition, pageWidth, margin);
    });

    // Línea separadora
    yPosition += 6;
    pdf.setLineWidth(0.25);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Datos de Acción Formativa
    if (data.accionFormativa) {
      if (this.necesitaNuevaPagina(yPosition, pageHeight)) {
        this.nuevaPagina(pdf, pageWidth, margin);
        yPosition = 30;
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('ACCIÓN FORMATIVA', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      const accionData = [
        ['Nombre:', data.accionFormativa.nombre || 'No especificado'],
        ['Expediente:', data.accionFormativa.expediente],
        ['Administración Pública:', data.accionFormativa.administracionPublica || 'No especificado'],
        ['Número de Alumnos:', data.accionFormativa.numAlumnos.toString()],
        ['Número de Horas:', data.accionFormativa.numHoras?.toString() || 'No especificado'],
        ['Fecha de Inicio:', this.formatDate(data.accionFormativa.fechaInicio)],
        ['Fecha de Fin:', this.formatDate(data.accionFormativa.fechaFin)],
        ['Comunidad Autónoma:', data.accionFormativa.ccaa],
        ['Ubicación de los cursos:', data.accionFormativa.ubicacion || 'No especificado'],
        ['Prácticas:', data.accionFormativa.practicas || 'No especificado'],
        ['Modalidad:', data.accionFormativa.modalidad || 'No especificado'],
        ['Cursos:', htmlToPlainText(data.accionFormativa.cursosText)]
      ];

      accionData.forEach(([label, value]) => {
        // Si es el campo "Cursos" y tiene contenido HTML, renderizarlo con formato
        if (label === 'Cursos:' && data.accionFormativa && data.accionFormativa.cursosText && data.accionFormativa.cursosText.trim() !== '' && data.accionFormativa.cursosText.trim() !== '<p><br></p>' && data.accionFormativa.cursosText.trim() !== '<p><br /></p>') {
          if (this.necesitaNuevaPagina(yPosition, pageHeight)) {
            this.nuevaPagina(pdf, pageWidth, margin);
            yPosition = 30;
          }

          pdf.setFont('helvetica', 'bold');
          pdf.text(label, margin, yPosition);

          const textX = margin + 60;
          const textMaxWidth = pageWidth - margin - 60 - 10; // Ancho disponible para el texto

          // Renderizar HTML con formato
          yPosition = renderHtmlToPdf(data.accionFormativa.cursosText, {
            pdf,
            x: textX,
            y: yPosition,
            maxWidth: textMaxWidth,
            pageHeight,
            baseFontSize: 10,
            lineHeight: 6
          });
        } else {
          yPosition = this.addWrappedKeyValue(pdf, label, value, yPosition, pageWidth, margin);
        }
      });

      // Cursos de la Acción Formativa
      if (data.cursosAccionFormativa && data.cursosAccionFormativa.length > 0) {
        yPosition += 15;
        pdf.setFont('helvetica', 'bold');
        pdf.text('CURSOS ASOCIADOS:', margin, yPosition);
        yPosition += 8;

        data.cursosAccionFormativa.forEach((curso, index) => {
          if (this.necesitaNuevaPagina(yPosition, pageHeight)) {
            this.nuevaPagina(pdf, pageWidth, margin);
            yPosition = 30;
          }

          pdf.setFont('helvetica', 'bold');
          pdf.text(`Curso ${index + 1}:`, margin, yPosition);
          yPosition += 6;

          const cursoData = [
            ['  Nombre:', curso.nombre],
            ['  Expediente:', curso.expediente || 'No especificado'],
            ['  Modalidad:', curso.modalidad || 'No especificado'],
            ['  Horas:', curso.horas?.toString() || 'No especificado'],
            ['  Alumnos:', curso.numAlumnos.toString()],
            ['  Fecha de Inicio:', this.formatDate(curso.fechaInicio)],
            ['  Fecha de Fin:', this.formatDate(curso.fechaFin)],
            ['  Ubicación:', curso.ubicacion || 'No especificado'],
            ['  Prácticas:', curso.practicas || 'No especificado']
          ];

          cursoData.forEach(([label, value]) => {
            // Nota: Para los cursos anidados, margin + 40 es el offset hardcodeado originalmente
            yPosition = this.addWrappedKeyValue(pdf, label, value, yPosition, pageWidth, margin, 40);
          });

          yPosition += 5;
        });
      }
    }

    // Datos del Curso
    if (data.curso) {
      if (this.necesitaNuevaPagina(yPosition, pageHeight)) {
        this.nuevaPagina(pdf, pageWidth, margin);
        yPosition = 30;
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('CURSO', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      const cursoData = [
        ['Nombre:', data.curso.nombre],
        ['Expediente:', data.curso.expediente],
        ['Administración Pública:', data.curso.administracionPublica || 'No especificado'],
        ['Número de Alumnos:', data.curso.numAlumnos.toString()],
        ['Número de Horas:', data.curso.numHoras?.toString() || 'No especificado'],
        ['Fecha de Inicio:', this.formatDate(data.curso.fechaInicio)],
        ['Fecha de Fin:', this.formatDate(data.curso.fechaFin)],
        ['Comunidad Autónoma:', data.curso.ccaa],
        ['Ubicación:', data.curso.ubicacionCurso || 'No especificado'],
        ['Prácticas:', data.curso.practicas || 'No especificado'],
        ['Modalidad:', data.curso.modalidad || 'No especificado']
      ];

      cursoData.forEach(([label, value]) => {
        yPosition = this.addWrappedKeyValue(pdf, label, value, yPosition, pageWidth, margin);
      });
    }

    // Línea separadora
    pdf.setLineWidth(0.25);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Datos de la Póliza
    if (data.poliza) {
      if (this.necesitaNuevaPagina(yPosition, pageHeight)) {
        this.nuevaPagina(pdf, pageWidth, margin);
        yPosition = 30;
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('PÓLIZA DE SEGUROS', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      const polizaData = [
        ['Tipo de Póliza:', data.poliza.tipo],
        ['Forma de Pago:', data.poliza.formaPago],
        ['IBAN:', data.poliza.iban],
        ['Comentarios:', data.poliza.comentarios || 'Sin comentarios']
      ];

      polizaData.forEach(([label, value]) => {
        yPosition = this.addWrappedKeyValue(pdf, label, value, yPosition, pageWidth, margin);
      });
    }

    // Pie de página
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, 290, { align: 'right' });
      pdf.text(`Generado el ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')}`, margin, 290);
    }

    return pdf;
  },

  /**
   * Descarga el PDF en el dispositivo local
   * @param pdf - Objeto PDF de jsPDF
   * @param filename - Nombre del archivo
   */
  descargarPDF(pdf: jsPDF, filename: string = 'resumen-solicitud.pdf'): void {
    pdf.save(filename);
  },

  /**
   * Sube el PDF a la carpeta de archivos de la sociedad
   * @param pdf - Objeto PDF de jsPDF
   * @param sociedadId - ID de la sociedad
   * @param filename - Nombre del archivo
   * @returns Promise con la respuesta del servidor
   */
  async subirPDF(pdf: jsPDF, sociedadId: number, tipo: string = 'resumen'): Promise<PdfUploadResponse> {
    try {
      // Validar parámetros de entrada
      if (!sociedadId || sociedadId <= 0) {
        throw new Error(`sociedadId inválido: ${sociedadId}`);
      }

      // Convertir PDF a Blob
      const pdfBlob = pdf.output('blob');

      // Crear FormData (agregar PRIMERO los campos y al final el archivo)
      const formData = new FormData();
      formData.append('sociedadId', String(sociedadId)); // Campo primero
      formData.append('tipo', String(tipo)); // Campo después
      formData.append('pdf', pdfBlob); // Archivo al final

      // URL del endpoint
      const uploadUrl = buildApiUrl(API_ENDPOINTS.UPLOAD_PDF);

      // Enviar a la API
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: getUploadHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al subir PDF: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Genera y sube el PDF automáticamente (sin descarga local)
   * @param data - Datos completos de la solicitud
   * @param sociedadId - ID de la sociedad
   * @returns Promise con el resultado de la operación
   */
  async generarYSubirPDF(data: {
    sociedad: Sociedad;
    accionFormativa?: IAccionFormativa;
    cursosAccionFormativa?: ICursoAccionFormativa[];
    curso?: ICurso;
    poliza?: IPoliza;
  }, sociedadId: number): Promise<{ success: boolean; message: string; filename?: string }> {
    try {
      // Generar PDF
      const pdf = await this.generarPDFResumen(data);

      // Crear nombre de archivo único
      const filename = `resumen-solicitud-${sanitizeFilename(data.sociedad.razonSocial)}.pdf`;

      // Solo subir PDF al servidor (sin descarga local)
      await this.subirPDF(pdf, sociedadId);

      return {
        success: true,
        message: 'PDF generado y enviado exitosamente',
        filename
      };
    } catch (error) {
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  },

  /**
   * Genera, descarga y sube el PDF (acción manual del usuario)
   * @param data - Datos completos de la solicitud
   * @param sociedadId - ID de la sociedad
   * @returns Promise con el resultado de la operación
   */
  async generarDescargarYSubirPDF(data: {
    sociedad: Sociedad;
    accionFormativa?: IAccionFormativa;
    cursosAccionFormativa?: ICursoAccionFormativa[];
    curso?: ICurso;
    poliza?: IPoliza;
  }, sociedadId: number): Promise<{ success: boolean; message: string; filename?: string }> {
    try {
      // Generar PDF
      const pdf = await this.generarPDFResumen(data);

      // Crear nombre de archivo único
      const filename = `resumen-solicitud-${sanitizeFilename(data.sociedad.razonSocial)}.pdf`;

      // Descargar PDF localmente
      this.descargarPDF(pdf, filename);

      // Subir PDF al servidor
      await this.subirPDF(pdf, sociedadId);

      return {
        success: true,
        message: 'PDF generado y enviado exitosamente',
        filename
      };
    } catch (error) {
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  },

  /**
   * Obtiene el nombre de la asociación basado en el ID
   * @param id - ID de la asociación
   * @returns Nombre de la asociación
   */
  getAsociacionName(id: string): string {
    const asociaciones: { [key: string]: string } = {
      'AEGEF': 'ASOCIACION ESTATAL DE GRANDES EMPRESAS DE FORMACION',
      'CECAP-EXT': 'CECAP EXTREMADURA',
      'CECAP-CYL': 'CECAP CASTILLA Y LEON',
      'CECAP-AND': 'CECAP ANDALUCIA',
      'CECAP-VAL': 'CECAP COMUNIDAD VALENCIANA',
      'SIN-CECAP': 'SIN ASOCIACION',
      'FMASTER': 'FORMASTER'
    };
    return asociaciones[id] || id;
  },

  /**
   * Formatea una fecha para mostrar
   * @param date - Fecha a formatear
   * @returns Fecha formateada como string
   */
  formatDate(date: Date | null): string {
    if (!date) return 'No especificado';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  },

  /**
   * Agrega el logo de la empresa al PDF en la esquina superior derecha
   * @param pdf - Objeto PDF
   * @param pageWidth - Ancho de la página
   * @param margin - Margen de la página
   */
  async agregarLogo(pdf: jsPDF, pageWidth: number, margin: number): Promise<void> {
    try {
      // Cargar el logo desde la carpeta public
      const logoUrl = '/CG-Gestion-Logo-H.jpg';

      // Crear una imagen
      const img = new Image();
      img.crossOrigin = 'anonymous';

      return new Promise((resolve) => {
        img.onload = () => {
          try {
            // Dimensiones del logo (ajustadas para el logo horizontal)
            const logoWidth = 80;  // Aumentado para mejor visibilidad
            const logoHeight = 25; // Proporción mantenida

            // Posición del logo (esquina superior derecha)
            const logoX = pageWidth - margin - logoWidth;
            const logoY = 8; // Posicionado más arriba

            // Agregar imagen al PDF
            pdf.addImage(img, 'JPG', logoX, logoY, logoWidth, logoHeight);

            resolve();
          } catch (error) {
            resolve();
          }
        };

        img.onerror = () => {
          resolve();
        };

        img.src = logoUrl;
      });
    } catch (error) {
    }
  },

  /**
   * Verifica si se necesita una nueva página
   * @param yPosition - Posición vertical actual
   * @param pageHeight - Altura de la página
   * @returns true si se necesita nueva página
   */
  necesitaNuevaPagina(yPosition: number, pageHeight: number): boolean {
    return yPosition > pageHeight - 40; // 40px de margen inferior
  },

  /**
   * Crea una nueva página con encabezado
   * @param pdf - Objeto PDF
   * @param pageWidth - Ancho de la página
   * @param margin - Margen de la página
   */
  nuevaPagina(pdf: jsPDF, pageWidth: number, margin: number): void {
    pdf.addPage();
    // Agregar logo en la nueva página
    this.agregarLogo(pdf, pageWidth, margin);
  },

  /**
   * Agrega una sección con fondo de color
   * @param pdf - Objeto PDF
   * @param x - Posición X
   * @param y - Posición Y
   * @param width - Ancho
   * @param height - Altura
   * @param color - Color de fondo (hex)
   */
  agregarSeccionConFondo(pdf: jsPDF, x: number, y: number, width: number, height: number, color: string = '#f0f0f0'): void {
    // Establecer color de fondo
    pdf.setFillColor(color);
    pdf.rect(x, y, width, height, 'F');

    // Restaurar color original (negro)
    pdf.setFillColor(0, 0, 0);
  },

  /**
   * Agrega texto con formato especial
   * @param pdf - Objeto PDF
   * @param text - Texto a agregar
   * @param x - Posición X
   * @param y - Posición Y
   * @param options - Opciones de formato
   */
  agregarTextoFormateado(pdf: jsPDF, text: string, x: number, y: number, options: {
    fontSize?: number;
    fontStyle?: 'normal' | 'bold' | 'italic';
    color?: string;
    align?: 'left' | 'center' | 'right';
  } = {}): void {
    const { fontSize = 10, fontStyle = 'normal', color = '#000000', align = 'left' } = options;

    // Aplicar formato
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', fontStyle);
    pdf.setTextColor(color);

    // Agregar texto
    pdf.text(text, x, y, { align });
  },

  /**
   * Obtiene todos los PDFs de una sociedad
   * @param sociedadId - ID de la sociedad
   * @returns Promise con la lista de PDFs
   */
  async obtenerPDFsSociedad(sociedadId: number): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/formulario/pdf/sociedad/${sociedadId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error al obtener PDFs: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  /**
   * Elimina un PDF específico de una sociedad
   * @param sociedadId - ID de la sociedad
   * @param filename - Nombre del archivo a eliminar
   * @returns Promise con el resultado de la operación
   */
  async eliminarPDF(sociedadId: number, filename: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/formulario/pdf/sociedad/${sociedadId}/${filename}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error al eliminar PDF: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};
